import type { TextFieldProps } from '@mui/material';
import TextField from '@mui/material/TextField';
import {
  Control,
  Controller,
  ControllerProps,
  FieldError,
  Path
} from 'react-hook-form';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { useTranslation } from 'react-i18next';

/**
 * Componente TextFieldElement
 *
 * Campo de texto integrado con validaciones y react-hook-form.
 *
 * @component
 * @param {object} props - Props del componente.
 * @param {string} props.name - Nombre del campo en el formulario.
 * @param {object} [props.validation] - Reglas de validación.
 * @param {function} [props.parseError] - Función para parsear el error.
 * @param {Control} [props.control] - Control de react-hook-form.
 * @param {boolean} [props.requiredAndCaracter] - Requiere caracteres específicos.
 * @param {boolean} [props.requiredAndDayNatural] - Requiere día natural.
 * @param {string} [props.customValue] - Valor personalizado.
 * @example
 * <TextFieldElement name="usuario" control={control} />
 */

export type TextFieldElementProps<T> = Omit<TextFieldProps, 'name'> & {
  validation?: ControllerProps['rules'];
  name: Path<T>;
  parseError?: (error: FieldError) => string;
  control?: Control<T>;
  /** Si es true, el `Controller` de RHF deshabilita el campo (no valida en submit). */
  controllerDisabled?: boolean;
  requiredAndCaracter?: boolean;
  requiredAndDayNatural?: boolean;
  customValue?: string;
};

export function TextFieldElement<TFieldValues extends FieldValues>({
  validation = {},
  parseError,
  type,
  required,
  controllerDisabled = false,
  requiredAndCaracter,
  requiredAndDayNatural,
  name,
  control,
  customValue = null,
  ...rest
}: TextFieldElementProps<TFieldValues>) {
  const { t, i18n } = useTranslation('global');
  const { inputProps: inputPropsFromRest, ...textFieldRest } = rest;
  if (required && !validation.required) {
    validation.required = t('common.commonMessages.requiredField');
  }

  if (requiredAndCaracter && !validation.required) {
    validation.required = t('admin.catalogs.currency.requiredField');
  }

  if (requiredAndDayNatural && !validation.required) {
    validation.required = t('common.commonMessages.requiredAndDayNatural');
  }

  if (type === 'email' && !validation.pattern) {
    validation.pattern = {
      value:
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      message: 'Formato inválido'
    };
  }

  return (
    <Controller
      control={control}
      disabled={controllerDisabled}
      name={name}
      render={({
        field: { value, onChange, onBlur },
        fieldState: { invalid, error }
      }) => (
        <TextField
          {...textFieldRest}
          error={invalid}
          helperText={
            error
              ? typeof parseError === 'function'
                ? parseError(error)
                : error.message
              : textFieldRest.helperText
          }
          inputProps={{
            ...rest.inputProps,
            lang: i18n.language
          }}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          sx={{
            ...(textFieldRest?.sx ?? {}),
            textarea: {
              padding: '5px'
            }
          }}
          type={type}
          value={customValue ?? value ?? ''}
          variant={textFieldRest?.variant || 'standard'}
        />
      )}
      rules={validation}
    />
  );
}

export default TextFieldElement;
